import { config } from '../../config';


type Callback<T = any> = (data: T) => void;



interface SubscriptionOptions {
    onError?: (error: Error) => void;
    autoReconnect?: boolean;
    reconnectDelay?: number;
    maxReconnectAttempts?: number;
    debounceMs?: number;
}

interface Subscription {
    eventSource: EventSource;
    callbacks: Set<Callback>;
    topics: string[];
    options: SubscriptionOptions;
    reconnectAttempts?: number;
    debounceTimer?: ReturnType<typeof setTimeout>;
    pendingData?: any;
}

interface MercureConfig {
    mercureUrl: string;
    apiUrl: string;
    token: string;
}

class MercureService {
    private subscriptions = new Map<string, Subscription>();
    private readonly baseUrl: string;

    constructor(baseUrl?: string) {
        this.baseUrl = baseUrl || config.apiUrl as string;
        if (!this.baseUrl) {
            throw new Error('[MercureService] REACT_APP_API_HOST not defined');
        }
    }

    /**
     * Subscribe to Mercure topics
     * @returns The subscription key to unsubscribe
     */
    async subscribe<T = any>(
        topics: string[],
        callback: Callback<T>,
        options: SubscriptionOptions = {}
    ): Promise<string> {
        const key = this.getKey(topics);

        // If already subscribed, just add the callback
        const existing = this.subscriptions.get(key);
        if (existing) {
            existing.callbacks.add(callback);
            return key;
        }

        // Create new connection
        const config = await this.fetchToken(topics);
        const eventSource = this.createEventSource(config, topics);

        const subscription: Subscription = {
            eventSource,
            callbacks: new Set([callback]),
            topics,
            options: { reconnectDelay: 3000, maxReconnectAttempts: 5, ...options },
        };

        this.setupEventHandlers(key, subscription);
        this.subscriptions.set(key, subscription);

        return key;
    }

    /**
     * Unsubscribe
     */
    unsubscribe(key: string, callback?: Callback): void {
        const sub = this.subscriptions.get(key);
        if (!sub) return;

        if (callback) {
            sub.callbacks.delete(callback);
        }

        // Close if no more callbacks
        if (!callback || sub.callbacks.size === 0) {
            this.close(key);
        }
    }

    private getKey(topics: string[]): string {
        return topics.sort().join('|');
    }

    private async fetchToken(topics: string[]): Promise<MercureConfig> {
        const authToken = localStorage.getItem(config.tokenKey) ?? process.env.REACT_APP_TOKEN_KEY;
        if (!authToken) {
            throw new Error('[MercureService] Authentication token missing');
        }

        try {
            const res = await fetch(`${this.baseUrl}/api/signal/mercure/token`, {
                method: 'POST',
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${authToken}`,
                },
                body: JSON.stringify({ topics }),
            });

            if (!res.ok) {
                throw new Error(`Failed to fetch token: ${res.status} ${res.statusText}`);
            }

            return await res.json() as MercureConfig;
        } catch (error) {
            console.error('[MercureService] fetchToken error:', error);
            throw error;
        }
    }

    private createEventSource(config: MercureConfig, topics: string[]): EventSource {
        const url = new URL(config.mercureUrl);
        topics.forEach((t) => url.searchParams.append('topic', t));
        url.searchParams.append('authorization', config.token);
        return new EventSource(url.toString());
    }

    private setupEventHandlers(key: string, sub: Subscription): void {
        const { eventSource, callbacks, options } = sub;

        eventSource.onopen = () => {
            sub.reconnectAttempts = 0;
        };

        eventSource.onmessage = (e: MessageEvent) => {
            const data = JSON.parse(e.data);

            // Si debounce activé
            if (options.debounceMs && options.debounceMs > 0) {
                sub.pendingData = data;

                if (sub.debounceTimer) {
                    clearTimeout(sub.debounceTimer);
                }

                sub.debounceTimer = setTimeout(() => {
                    callbacks.forEach((cb) => {
                        try {
                            cb(sub.pendingData);
                        } catch (error) {
                            console.error('[MercureService] Callback error:', error);
                        }
                    });
                    sub.debounceTimer = undefined;
                }, options.debounceMs);
            } else {
                // Comportement normal sans debounce
                callbacks.forEach((cb) => {
                    try {
                        cb(data);
                    } catch (error) {
                        console.error('[MercureService] Callback error:', error);
                    }
                });
            }
        };
        eventSource.onerror = () => {
            console.error('[MercureService] Error:', sub.topics);
            eventSource.close();

            const err = new Error('Connection lost');
            options.onError?.(err);

            sub.reconnectAttempts = (sub.reconnectAttempts || 0) + 1;

            if (
                options.autoReconnect &&
                sub.reconnectAttempts < (options.maxReconnectAttempts || 5)
            ) {
                setTimeout(() => {
                    this.reconnect(key, sub);
                }, options.reconnectDelay || 3000);
            } else {
                this.subscriptions.delete(key);
            }
        };
    }

    private async reconnect(key: string, oldSub: Subscription): Promise<void> {
        const { topics, callbacks, options } = oldSub;

        try {
            oldSub.eventSource?.close();

            const config = await this.fetchToken(topics);
            const eventSource = this.createEventSource(config, topics);

            const newSub: Subscription = {
                eventSource,
                callbacks,
                topics,
                options,
                reconnectAttempts: oldSub.reconnectAttempts
            };

            this.setupEventHandlers(key, newSub);
            this.subscriptions.set(key, newSub);
        } catch (err) {
            console.error('[MercureService] Reconnection failed:', err);

            options.onError?.(err as Error);

            // Remove subscription if max attempts reached
            if ((oldSub.reconnectAttempts ?? 0) >= (options.maxReconnectAttempts || 5)) {
                this.subscriptions.delete(key);
            }
        }
    }

    private close(key: string): void {
        const sub = this.subscriptions.get(key);
        if (!sub) return;

        sub.eventSource.close();
        this.subscriptions.delete(key);
        console.log('[MercureService] Closed:', sub.topics);
    }
}

export default new MercureService();
